/**
 * The one place SellGPT talks to CashitV3.
 *
 * Every endpoint below already exists and is already used by cashit.in and the mobile apps, except the
 * three under /api/v1/sellgpt. SellGPT deliberately adds no catalogue, pricing, OTP or lead logic of its
 * own: if a rule changes in Cashit, it changes here too, because there is nothing here to change.
 */

const API_BASE: string = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/+$/, '')

/** The envelope every Cashit endpoint returns. */
export interface ApiResponse<T> {
  success: boolean
  message: string
  data?: T
  meta?: unknown
}

/**
 * A failed call, carrying the server's own message and status.
 *
 * The server's message is written for a customer ("Incorrect OTP. 3 attempt(s) remaining.") and is shown
 * verbatim. Inventing a friendlier one here would hide exactly the detail the customer needs, and would
 * silently drift out of step with the rules the server is actually enforcing.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly meta?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the session has expired or was never valid, which the UI handles as sign-out, not failure. */
  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403
  }

  /** True when the request never reached the server at all. */
  get isOffline(): boolean {
    return this.status === 0
  }
}

/** Set by the auth store. Kept here so no caller has to remember to attach it. */
let bearerToken: string | null = null
export function setAuthToken(token: string | null): void {
  bearerToken = token
}

/** Called when any request comes back 401, so the app can drop a session the server no longer accepts. */
let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  /** Extra headers, for the idempotency key on lead creation. */
  headers?: Record<string, string>
  signal?: AbortSignal
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers }
  if (options.body !== undefined) headers['Content-Type'] = 'application/json'
  // SellGPT is a different origin from cashit.in, so it authenticates with a bearer token and never
  // relies on a cookie. `credentials` is left at its default ('same-origin'), which for a cross-origin
  // call sends nothing - that is the intent, not an oversight.
  if (bearerToken) headers['Authorization'] = `Bearer ${bearerToken}`

  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    throw new ApiError('We could not reach Cashit. Check your connection and try again.', 0)
  }

  // A body is expected on every status, including errors - but a proxy or an IIS error page may return
  // HTML, so a parse failure becomes a plain message rather than an unhandled exception in the page.
  let payload: ApiResponse<T> | null = null
  try {
    const text = await response.text()
    payload = text ? (JSON.parse(text) as ApiResponse<T>) : null
  } catch {
    payload = null
  }

  if (!response.ok || payload?.success === false) {
    if (response.status === 401) onUnauthorized?.()
    throw new ApiError(
      payload?.message || `Something went wrong (${response.status}).`,
      response.status,
      payload?.meta,
    )
  }

  return payload?.data as T
}

// ─────────────────────────────────────────────────────────────────────────────
//  SellGPT's own endpoints
// ─────────────────────────────────────────────────────────────────────────────

export interface SellGptConfig {
  assistantAvailable: boolean
  /** False when the server cannot transcribe speech, which hides the microphone rather than showing a dud button. */
  voiceInputAvailable: boolean
  leadSource: string
  otpExpiryMinutes: number
  otpResendDelaySeconds: number
  otpMaxAttempts: number
}

export const getConfig = (signal?: AbortSignal) =>
  request<SellGptConfig>('/api/v1/sellgpt/config', { signal })

export interface ChatTurn {
  role: 'user' | 'assistant'
  content: string
}

export interface ChatToolResult {
  tool: string
  result: unknown
}

export interface ChatReply {
  reply: string
  assistantAvailable: boolean
  unavailableReason?: string
  tools: ChatToolResult[]
}

/**
 * What the sale has already established, sent with every turn.
 *
 * The server replays only the TEXT of the conversation to the model, so without this the assistant begins
 * each turn knowing nothing it previously looked up - it re-searches the catalogue every message, and one
 * unlucky phrasing makes it tell the customer their device is not in the catalogue several turns after it
 * found it. Every id here came from a tool in the first place, and the server re-validates all of it.
 */
export interface ChatContext {
  deviceConfigId?: number
  deviceName?: string
  deviceVariantId?: number
  durationTimeId?: number
  deviceConditionIds?: number[]
  functionalIssueIds?: number[]
  accessoryIds?: number[]
  accessoriesAssessed?: boolean
  pincode?: string
  hasQuote?: boolean
  /** The language detected from the customer speaking, so the assistant keeps answering in it. */
  language?: string
}

export const chat = (
  message: string,
  history: ChatTurn[],
  context?: ChatContext,
  signal?: AbortSignal,
) =>
  request<ChatReply>('/api/v1/sellgpt/chat', { method: 'POST', body: { message, history, context }, signal })

export interface PickupAvailability {
  pincode: string
  serviceable: boolean
  area?: string
  areas: string[]
  city?: string
  state?: string
  cityId?: number
}

/**
 * Catalogue names close to a search that found nothing - "did you mean Dell Latitude?".
 * Used only after a search returns nothing, so it never competes with the real ranking.
 */
export interface Transcript {
  /** False when voice input is not configured on the server, so the UI hides the microphone entirely. */
  available: boolean
  /** What the customer said. Empty when nothing could be transcribed - which is not an error. */
  text: string
  /**
   * The language the server DETECTED, as OpenAI names it ("english", "hindi", "tamil"). Nothing asks the
   * customer to choose one; it is sent on to the assistant so the reply comes back in the same language.
   */
  language?: string | null
}

/**
 * Speech to text. The recording goes to Cashit, never to a third party from this page - the OpenAI key
 * lives only on the server, which is the rule the whole of SellGPT is built on.
 *
 * Multipart rather than JSON, so the audio is not base64-inflated by a third on its way up a mobile
 * connection. That is why this does not use `request`: it must not set a JSON content type, and the
 * browser has to choose the multipart boundary itself.
 */
export async function transcribe(audio: Blob, language?: string, signal?: AbortSignal): Promise<Transcript> {
  const form = new FormData()
  form.append("audio", audio, fileNameFor(audio.type))
  // Empty means "detect it", which is the default. The server ignores anything not on its own list.
  if (language) form.append("language", language)

  const headers: Record<string, string> = { Accept: "application/json" }
  if (bearerToken) headers["Authorization"] = `Bearer ${bearerToken}`

  let response: Response
  try {
    response = await fetch(`${API_BASE}/api/v1/sellgpt/transcribe`, {
      method: "POST",
      headers,
      body: form,
      signal,
    })
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error
    throw new ApiError("We could not reach Cashit. Check your connection and try again.", 0)
  }

  let payload: ApiResponse<Transcript> | null = null
  try {
    const text = await response.text()
    payload = text ? (JSON.parse(text) as ApiResponse<Transcript>) : null
  } catch {
    payload = null
  }

  if (!response.ok || payload?.success === false) {
    if (response.status === 401) onUnauthorized?.()
    throw new ApiError(payload?.message ?? "We could not understand that recording.", response.status)
  }

  return payload?.data ?? { available: true, text: "" }
}

/** The extension the server maps back to a content type. The browser picks the container, not us. */
function fileNameFor(mimeType: string): string {
  const type = mimeType.split(";")[0].trim().toLowerCase()
  if (type === "audio/ogg") return "speech.ogg"
  if (type === "audio/mp4") return "speech.mp4"
  if (type === "audio/mpeg" || type === "audio/mp3") return "speech.mp3"
  if (type === "audio/wav" || type === "audio/x-wav") return "speech.wav"
  return "speech.webm"
}

export const getSuggestions = (query: string, categoryId?: number, signal?: AbortSignal) => {
  const params = new URLSearchParams({ q: query })
  if (categoryId) params.set('categoryId', String(categoryId))
  return request<string[]>(`/api/v1/sellgpt/did-you-mean?${params}`, { signal })
}

export const checkPickup = (pincode: string, signal?: AbortSignal) =>
  request<PickupAvailability>(`/api/v1/sellgpt/pickup/${encodeURIComponent(pincode)}`, { signal })

// ─────────────────────────────────────────────────────────────────────────────
//  Catalogue — existing Cashit endpoints, unchanged
// ─────────────────────────────────────────────────────────────────────────────

export interface Category {
  id: number
  categoryName: string
  thumbImage?: string
  categoryUrl?: string
  brandCount: number
}

export const getCategories = (signal?: AbortSignal) =>
  request<Category[]>('/api/v1/home/categories', { signal })

export interface SearchResult {
  id: number
  name: string
  image?: string
  brand?: string
  category?: string
  resultType: 'device' | 'brand' | 'category'
  /** The model group this device belongs to; its other configurations hang off it. */
  modelGroupId?: number
  configurationCount?: number
  minSellPrice?: number
  maxSellPrice?: number
}

export const searchDevices = (query: string, categoryId?: number, signal?: AbortSignal) => {
  const params = new URLSearchParams({ q: query, limit: '8' })
  if (categoryId) params.set('categoryId', String(categoryId))
  return request<SearchResult[]>(`/api/v1/home/search?${params}`, { signal })
}

/**
 * One priced configuration of a model group - the RAM/storage choice a customer makes after picking a
 * suggestion. For phones and tablets each configuration is its OWN DeviceConfig, so without asking this
 * the sale silently lands on whichever one the search happened to return: "Apple iPhone 15" became the
 * 128 GB at Rs 46,100 even for someone holding the 256 GB at Rs 44,700.
 */
export interface ModelGroupConfiguration {
  deviceId: number
  name: string
  image?: string
  ramGb?: number
  storageGb?: number
  sellPrice: number
}

export const getModelConfigurations = (modelGroupId: number, signal?: AbortSignal) =>
  request<ModelGroupConfiguration[]>(
    `/api/v1/home/search/configurations?modelGroupId=${modelGroupId}`,
    { signal },
  )

export interface Option {
  id: number
  label: string
}

export interface ModelConfiguration {
  deviceVariantId: number
  name: string
  processorId?: number
  ramGb?: number
  storageGb?: number
  isDefault: boolean
}

/** The shape SellGPT needs out of /sell/assessment, which returns a great deal more. */
export interface DeviceAssessment {
  deviceConfigId: number
  deviceName: string
  deviceImage?: string
  fromPrice: number
  hasModelVariants: boolean
  selectedDeviceVariantId?: number
  modelVariants: Array<{
    deviceVariantId: number
    name: string
    processorId?: number
    ramGb?: number
    storageGb?: number
    isDefault: boolean
  }>
  durationTimes: Array<{ id: number; durationName: string; defaultSelected: boolean }>
  conditions: Array<{ id: number; conditionName: string }>
  functionalProblems: Array<{ id: number; problemName: string; labelName?: string }>
  accessories: Array<{ accessoryId: number; name: string; labelName?: string }>
  /**
   * The full component catalogue for this category - every RAM, processor and drive Cashit prices. Offered
   * so a customer whose laptop has been upgraded since it left the factory can say so; the configuration
   * above is what the model SHIPPED as, which is not always what they are holding.
   */
  components?: {
    /** The category the full component lists are fetched for. */
    categoryId?: number
    rams?: ComponentOption[]
    processors?: ComponentOption[]
    hardDrives?: ComponentOption[]
  }
}

export interface ComponentOption {
  id: number
  name: string
  image?: string
  /** The component this model shipped with, where the catalogue records one. */
  isDefault?: boolean
  /** True when this is the component currently selected for the device. */
  isSelected?: boolean
}

/**
 * Every component Cashit prices for a model - not merely the one it shipped with.
 *
 * A laptop that has been upgraded is the normal case, not an exotic one, and cashit.in asks for the full
 * specification as step 1 of its laptop assessment. The full lists are large (165 processors for a Windows
 * laptop), which is why the UI shows the current choice and lets it be CHANGED rather than printing them.
 */
export interface DeviceComponents {
  categoryId: number
  rams: ComponentOption[]
  processors: ComponentOption[]
  hardDrives: ComponentOption[]
  screenSizes?: ComponentOption[]
  modelRequired?: boolean
}

export const getComponents = (categoryId: number, deviceConfigId: number, signal?: AbortSignal) =>
  request<DeviceComponents>(
    `/api/v1/sell/components/${categoryId}?deviceConfigId=${deviceConfigId}`,
    { signal },
  )

export const getAssessment = (deviceConfigId: number, signal?: AbortSignal) =>
  request<DeviceAssessment>(`/api/v1/sell/assessment/${deviceConfigId}`, { signal })

// ─────────────────────────────────────────────────────────────────────────────
//  Valuation — the ONLY source of a price shown anywhere in SellGPT
// ─────────────────────────────────────────────────────────────────────────────

export interface PriceQuoteRequest {
  deviceConfigId: number
  deviceVariantId?: number
  durationTimeId?: number
  deviceConditionIds?: number[]
  functionalIssueIds?: number[]
  accessoryIds?: number[]
  accessoriesAssessed?: boolean
  processorId?: number
  ramId?: number
  hardDriveId?: number
  screenSizeId?: number
  promoCode?: string
}

export interface PriceQuote {
  deviceConfigId: number
  deviceName: string
  deviceVariantId?: number
  variantName?: string
  finalPrice: number
  sellerPayout: number
  couponBonus: number
  couponCode?: string
  couponRejectedReason?: string
  eligibility: 'Eligible' | 'ManualReview' | 'Ineligible'
  eligibilityReasons: string[]
  requiresManualValuation: boolean
  condition: string
}

export const getPriceQuote = (body: PriceQuoteRequest, signal?: AbortSignal) =>
  request<PriceQuote>('/api/v1/sell/price-quote', { method: 'POST', body, signal })

// ─────────────────────────────────────────────────────────────────────────────
//  Authentication — Cashit's existing OTP service
// ─────────────────────────────────────────────────────────────────────────────

/** The purpose string the OTP is issued and verified under. Both calls must agree or verification fails. */
export const OTP_PURPOSE = 'Login'

export const sendOtp = (mobileNumber: string, signal?: AbortSignal) =>
  request<{ otpSent: boolean; expiresInMinutes: number }>('/api/v1/auth/send-otp', {
    method: 'POST',
    body: { mobileNumber, otpPurpose: OTP_PURPOSE },
    signal,
  })

export interface VerifiedNewUser {
  verified: true
  isNewUser: true
  mobileNumber: string
}

export interface VerifiedCustomer {
  verified: true
  isNewUser: false
  customerId: number
  customerNo?: string
  customerName?: string
  mobileNumber: string
  profileImage?: string
  isActive?: boolean
  token: string
}

export type VerifyOtpResult = VerifiedNewUser | VerifiedCustomer

export const verifyOtp = (mobileNumber: string, otpCode: string, signal?: AbortSignal) =>
  request<VerifyOtpResult>('/api/v1/auth/verify-otp', {
    method: 'POST',
    body: { mobileNumber, otpCode, otpPurpose: OTP_PURPOSE },
    signal,
  })

export interface RegisteredCustomer {
  id: number
  customerId?: string
  customerName?: string
  mobileNumber: string
  emailId?: string
  token: string
}

export const register = (
  body: { customerName: string; mobileNumber: string; emailId?: string },
  signal?: AbortSignal,
) => request<RegisteredCustomer>('/api/v1/auth/register', { method: 'POST', body, signal })

export interface CustomerProfile {
  id: number
  customerId?: string
  customerName?: string
  mobileNumber?: string
  emailId?: string
  profileImage?: string
  isActive: boolean
  regDate?: string
}

export const getProfile = (customerRegId: number, signal?: AbortSignal) =>
  request<CustomerProfile>(`/api/v1/auth/profile/${customerRegId}`, { signal })

// ─────────────────────────────────────────────────────────────────────────────
//  Addresses and leads — existing Cashit endpoints
// ─────────────────────────────────────────────────────────────────────────────

export interface Address {
  id: number
  addressTypeId?: number
  cityId?: number
  cityName?: string
  stateName?: string
  flatHouseNo?: string
  fullAddress?: string
  pincode?: number
  landmark?: string
}

export const getAddresses = (customerRegId: number, signal?: AbortSignal) =>
  request<Address[]>(`/api/v1/address/customer/${customerRegId}`, { signal })

export const saveAddress = (
  body: {
    customerRegId: number
    addressTypeId?: number
    cityId?: number
    flatHouseNo?: string
    fullAddress?: string
    pincode?: number
    landmark?: string
  },
  signal?: AbortSignal,
) => request<{ id: number }>('/api/v1/address', { method: 'POST', body, signal })

export interface CreateLeadRequest extends PriceQuoteRequest {
  addressBookId: number
  /** Always 'sellgpt.in'. Refused by the server if it is not a value it recognises. */
  leadSource: string
  loginDevice?: string
  leadRemark?: string
  /** The collection window the customer asked for, as local wall-clock time (see pickupSlots.ts). */
  pickupFrom?: string
  pickupToDate?: string
}

export interface CreatedLead {
  leadId: number
  leadNumber: string
  message: string
  isCustomConfig: boolean
  requiresManualValuation: boolean
  sellerPayout: number
  deviceConfigId: number
  deviceVariantId?: number
}

/**
 * Creates the lead. The idempotency key is REQUIRED by SellGPT's own flow rather than by the server:
 * a customer who taps "confirm" twice, or whose connection drops after the request left the browser,
 * must not end up with two sale requests for one device. The key is generated once when the
 * confirmation screen opens and reused for every retry of that same submission.
 */
export const createLead = (body: CreateLeadRequest, idempotencyKey: string, signal?: AbortSignal) =>
  request<CreatedLead>('/api/v1/sell/create-lead', {
    method: 'POST',
    body,
    headers: { 'Idempotency-Key': idempotencyKey },
    signal,
  })

export interface CustomerLead {
  id: number
  leadNumber: string
  deviceName?: string
  deviceImage?: string
  statusName?: string
  leadDate?: string
  totalAmount?: number
  pickupFrom?: string
  /** Which front end the sale was started from (migration 98). Null for a lead that names none. */
  leadSource?: string
}

export const getCustomerLeads = (customerRegId: number, signal?: AbortSignal) =>
  request<CustomerLead[]>(`/api/v1/leads/customer/${customerRegId}`, { signal })

// ─────────────────────────────────────────────────────────────────────────────
//  After the sale is placed: tracking it through to completion
// ─────────────────────────────────────────────────────────────────────────────

export interface LeadJourneyEntry {
  id: number
  statusName?: string
  remark?: string
  addedDate?: string
}

export interface LeadDetail {
  id: number
  leadNumber: string
  deviceName?: string
  deviceImage?: string
  statusName?: string
  totalAmount?: number
  leadDate?: string
  pickupFrom?: string
  pickupToDate?: string
  brandName?: string
  categoryName?: string
  deviceCost?: number
  couponAmount?: number
  couponCode?: string
  conditionName?: string
  durationName?: string
  address?: string
  cityName?: string
  /** Which front end the sale was started from (migration 98). Null for a lead that names none. */
  leadSource?: string
  functionalIssues: string[]
  accessories: string[]
  /** Newest first, and only the entries Cashit has marked visible to the customer. */
  journey: LeadJourneyEntry[]
}

export const getLead = (leadNumber: string, signal?: AbortSignal) =>
  request<LeadDetail>(`/api/v1/leads/${encodeURIComponent(leadNumber)}`, { signal })

/**
 * One valuation version of a sale. A second version appears when Cashit inspects the device and its
 * condition differs from what was described - the customer then accepts or rejects that revised amount,
 * and accepting is the ONLY way it becomes the amount they are paid.
 */
export interface QuoteVersion {
  version: number
  status: 'Proposed' | 'Accepted' | 'Rejected' | 'Expired' | 'Superseded'
  source: string
  deviceValue: number
  couponBonus: number
  sellerPayout: number
  previousDeviceValue?: number
  requiresManualValuation: boolean
  reason?: string
  createdOn: string
  expiresOn?: string
  decidedOn?: string
  /** Sent back on accept/reject so a quote that changed while it was on screen is refused, not decided. */
  stateVersion: number
  isCurrent: boolean
}

export const getQuoteVersions = (leadNumber: string, signal?: AbortSignal) =>
  request<QuoteVersion[]>(`/api/v1/customer/leads/${encodeURIComponent(leadNumber)}/quotes`, { signal })

export const decideQuote = (
  leadNumber: string,
  version: number,
  decision: 'accept' | 'reject',
  stateVersion: number,
  reason?: string,
  signal?: AbortSignal,
) =>
  request<{ leadNumber: string; version: number; status: string }>(
    `/api/v1/customer/leads/${encodeURIComponent(leadNumber)}/quotes/${version}/${decision}`,
    { method: 'POST', body: { stateVersion, reason }, signal },
  )

export const cancelLead = (leadId: number, reason: string, signal?: AbortSignal) =>
  request<{ cancelled: boolean }>('/api/v1/leads/cancel', {
    method: 'POST',
    body: { leadId, reason },
    signal,
  })
